import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isMobileBrowser } from 'app/utils/browser';
import { formValueSelector } from 'redux-form';
import { FormattedMessage } from 'translations';
import { connectSubpages } from 'app/hocs/subpageManagable';
import WaitingTime from '../WaitingTime';
import { preprocessTripObject } from 'app/utils/trip';
import { Layout, Flex } from 'app/components/base/Layout';
import Button from 'app/components/base/Button';
import TripProviderLogo from 'app/components/search-results/Trip/commons/TripProviderLogo';
import VehicleModel from 'app/components/common/VehicleModel';
import MilesEarnedLabel from '../MilesEarnedLabel';
import classnames from 'classnames';
import config from 'config';
import {
  BOOK_TRIP_FORM,
  DEPARTING_TRIP_FIELD_NAME,
  PRICE_FIELD_NAME,
} from 'app/constants';

import style from './style.css';

const messages = {
  details: { id: 'BOOK_TRIP.TRIP_DETAILS_BTN_TEXT' },
  waitTime: { id: 'BOOK_TRIP.WAIT_TIME' },
};

export const mapStateToProps = (state, { formName = BOOK_TRIP_FORM }) => {
  const selector = formValueSelector(formName);
  const trip = selector(state, DEPARTING_TRIP_FIELD_NAME);
  const price = selector(state, PRICE_FIELD_NAME);
  const preparedTrip = preprocessTripObject(trip);

  if (preparedTrip) {
    const { heading, details, pricing } = preparedTrip;
    const rewardPoints = price?.rewardPoints || details.rewardPoints;

    const newPricing = {
      ...pricing,
      ridePrice: pricing.price,
      finalPrice: price?.finalPrice || pricing.price,
    };
    return {
      heading,
      details,
      pricing: newPricing,
      rewardPoints,
    };
  }
  return {};
};

/**
 * Component used in the BookTrip page to display a summary of the
 * trip and provider information for tablet and mobile versions.
 */
export class TripSummary extends Component {
  static defaultProps = {
    showTotalPrice: true,
  };

  handleDetailsClick = () => {
    const { subpages, detailsComponent, readOnly, showTotalPrice, formName } =
      this.props;
    subpages.pushPage(detailsComponent, { readOnly, showTotalPrice, formName });
  };

  get waitTimeElement() {
    const waitTime = this.props.details.detailedWaitingTime;
    return (
      (waitTime.return || waitTime.direct) && (
        <div className={style.waitingTime}>
          <WaitingTime waitTime={waitTime} />
        </div>
      )
    );
  }

  render() {
    const {
      heading,
      details,
      pricing,
      className,
      rewardPoints,
      showTotalPrice,
    } = this.props;

    if (!heading) return null;

    const { numVehicles } = details;
    const providerLogoProps = {
      numVehicles,
      imageUrl: heading.vehicleImageUrl,
      norating: true,
      medium: true,
    };

    const names = heading.providerNames;
    const descr = heading.description || (
      <FormattedMessage id={`VEHICLES.ID_${heading.category.id}.NAME`} />
    );

    return (
      <div className={classnames(style.container, className)}>
        <Layout className={style.dataWrapper} direction="column" nowrap>
          <Layout direction="row" align="start center" nowrap>
            <TripProviderLogo {...providerLogoProps} />
            <Flex size="grow" className={style.headingContainer}>
              {config.SHOW_PROVIDER_NAME_IN_PRICE_BREAKDOWN && (
                <h2 className={style.heading}>
                  <span>
                    {names.map((name, index) => (
                      <React.Fragment key={index}>
                        <span>{name}</span>
                        {index !== names.length - 1 ? (
                          <span className={style.separator} />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </span>
                  {numVehicles > 1 && <span> {`(${numVehicles})`} </span>}
                </h2>
              )}
              {config.SHOW_PROVIDER_NAME_IN_PRICE_BREAKDOWN && !!descr && (
                <span className={style.description}>{descr}</span>
              )}
              {config.SHOW_VEHICLE_DETAILS && (
                <VehicleModel
                  className={style.model}
                  make={heading.make}
                  model={heading.model}
                />
              )}
            </Flex>
            {!isMobileBrowser && this.waitTimeElement}
            <Layout direction="column" nowrap>
              {showTotalPrice && (
                <div className={style.price}>
                  <span>{pricing.finalPrice}</span>
                </div>
              )}
              <Button
                className={style.button}
                nocaps
                rounded
                bordered
                transparent
                onClick={this.handleDetailsClick}
              >
                <FormattedMessage {...messages.details} />
              </Button>
            </Layout>
          </Layout>
          {isMobileBrowser && this.waitTimeElement}
        </Layout>
        {config.LOYALTY_PROGRAM_ENABLED && !!rewardPoints && (
          <MilesEarnedLabel miles={rewardPoints} />
        )}
      </div>
    );
  }
}

// TODO: move to Flow types
// TripSummary.propTypes = {
//   formName: PropTypes.string,
//   heading: PropTypes.object,
//   pricing: PropTypes.object,
//   details: PropTypes.object,
//   readOnly: PropTypes.bool,
//   className: PropTypes.string,

//   detailsComponent: PropTypes.func.isRequired,
//   subpages: PropTypes.object.isRequired
// };

export default compose(
  connectSubpages(),
  connect(mapStateToProps)
)(TripSummary);
